import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import localize from "../../../common/localize";
import { Add, ArrowForward } from "@mui/icons-material";
import NoDataPage from "../../../components/noDataPage/NoDataPage";
import useGlobalContext from "../../../hooks/useGlobalContext";
import { getClearGeneralRequestStatusChange } from "../../../common/EntitiesManagement";
import ModalBackground from "../../../components/common/ModalBackground";
import LoadingPanel from "../../../components/common/LoadingPanel";
import RequestStatus from "../../../components/common/RequestStatus";
import { getInputLabelProps, getInputProps } from "../../../common/appStyles";
import Combobox from "../../../components/common/Combobox";
import {
  GeneralRequestStatusEnum,
  getBackColor,
  getForeColor,
  getStatusText,
} from "../../../common/utils";
import StatusChangeTimeLine from "../../event/components/StateChangeTimeline";

export default function RequestStatusChange({ requestModel, setRequestModel }) {
  const { config, GENERAL_REQUESTS_ENDPOINT, createEntity } =
    useGlobalContext();
  const [statusChanges, setStatusChanges] = useState([]);
  const [statusChange, setStatusChange] = useState(
    getClearGeneralRequestStatusChange(requestModel?.Status, requestModel?.Id)
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const statusOptions = [
    {
      Id: GeneralRequestStatusEnum.InProgress,
      Name: getStatusText(GeneralRequestStatusEnum.InProgress),
    },
    {
      Id: GeneralRequestStatusEnum.Done,
      Name: getStatusText(GeneralRequestStatusEnum.Done),
    },
    {
      Id: GeneralRequestStatusEnum.NotFinished,
      Name: getStatusText(GeneralRequestStatusEnum.NotFinished),
    },
  ];

  const getStatusChanges = async () => {
    try {
      setIsLoading(true);
      const result = await axios.get(
        `${GENERAL_REQUESTS_ENDPOINT}/${requestModel?.Id}/statusChange`,
        {
          ...config,
          Pagination: -1,
        }
      );

      if (
        result?.data?.Ok &&
        result?.data?.Data &&
        result?.data?.Data?.length > 0
      ) {
        setStatusChanges([...result.data.Data]);
      }
    } catch (exception) {
      console.error("Error loading request status changes: ", exception);
    } finally {
      setIsLoading(false);
    }
  };

  const addStatusChange = () => {
    setStatusChange(
      getClearGeneralRequestStatusChange(requestModel?.Status, requestModel?.Id)
    );
    setShowAddModal(true);
  };

  const handleChange = (e) => {
    setStatusChange({
      ...statusChange,
      [e.target.name]: e.target.value,
    });
  };

  const createStatusChange = async (statusChangeModel) => {
    const result = await createEntity(
      `${GENERAL_REQUESTS_ENDPOINT}/${requestModel?.Id}/statusChange`,
      {
        ...statusChangeModel,
        GeneralRequestId: requestModel?.Id,
      },
      config
    );

    validateOperationResult(result, false);
  };

  const validateOperationResult = (result, isDelete) => {
    setStatusChange(
      getClearGeneralRequestStatusChange(requestModel?.Status, requestModel?.Id)
    );

    if (result.ok || result.Ok) {
      setRequestModel({
        ...requestModel,
        Status: statusChange.FinalStatusId,
      });
      getStatusChanges();
    }
  };

  const getStatusChangeOptions = (currentStatus) =>
    statusOptions.filter((x) => x.Id > currentStatus);

  const handleFinalStatusChange = (status) => {
    if (!status) {
      setStatusChange({
        ...statusChange,
        FinalStatus: null,
        FinalStatusId: status.Id,
      });
      return;
    }

    setStatusChange({
      ...statusChange,
      FinalStatus: status,
      FinalStatusId: status.Id,
    });
  };

  const showAddStatusChangeModal = () => {
    return (
      <ModalBackground
        title={localize.add_status_change}
        showModal={showAddModal}
        setShowModal={setShowAddModal}
        onClickToCancel={() => {
          setStatusChange(
            getClearGeneralRequestStatusChange(
              requestModel?.Status,
              requestModel?.Id
            )
          );
          setShowAddModal(false);
        }}
        onClickToSubmit={() => {
          createStatusChange(statusChange);
          setShowAddModal(false);
        }}
        Component={
          <Box className="flex flex-col gap-4 w-full my-2" sx={{ width: 450 }}>
            <Box className="flex gap-2 w-full items-center">
              <Box sx={{ width: 150 }}>
                <RequestStatus status={requestModel?.Status} />
              </Box>
              <ArrowForward />
              <FormControl required fullWidth>
                <Combobox
                  isRequired
                  id="NewStatus"
                  fieldLabel={localize.new_status}
                  fieldOptions={
                    getStatusChangeOptions(requestModel?.Status) ?? []
                  }
                  fieldValue={statusChange.FinalStatus}
                  handleValueChange={handleFinalStatusChange}
                />
              </FormControl>
            </Box>
            <FormControl fullWidth>
              <TextField
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={statusChange?.Observation}
                onChange={handleChange}
                size="small"
                fullWidth
                multiline
                rows={2}
                id="Observation"
                label={localize.observations}
                name="Observation"
                variant="outlined"
              />
            </FormControl>
          </Box>
        }
      />
    );
  };

  const getStatusChangeData = () => {
    if (statusChanges && statusChanges.length > 0) {
      const result = statusChanges.map((change) => ({
        Id: change.Id,
        ChangeDate: change.CreationDate,
        CreatedBy: change.CreatedBy,
        Observation: change.Observation,
        InitialStatus: {
          BackgroundColor: getBackColor(change.InitialStatus),
          ForegroundColor: getForeColor(change.InitialStatus),
          Name: getStatusText(change.InitialStatus),
        },
        FinalStatus: {
          BackgroundColor: getBackColor(change.FinalStatus),
          ForegroundColor: getForeColor(change.FinalStatus),
          Name: getStatusText(change.FinalStatus),
        },
      }));

      return result;
    }

    return [];
  };

  const getUI = () => {
    if (statusChanges && statusChanges?.length > 0) {
      return (
        <Box sx={{ width: "100" }} className="flex flex-col gap-2">
          {showAddModal && showAddStatusChangeModal()}

          <Box className="w-full flex gap-2 items-center">
            <Alert severity="info" sx={{ width: "100%" }}>
              <label className="text-xs">
                {localize.requests_status_change_description}
              </label>
            </Alert>

            {requestModel?.Status <= 1 && (
              <Button
                sx={{ width: "150px" }}
                size="medium"
                variant="contained"
                color="success"
                className="app_general_button_data app_success_button btn-sm "
                onClick={() => {
                  addStatusChange();
                }}
              >
                <Add style={{ width: "16px", height: "16px" }} />
                <div className="mr-1" />
                {localize.add}
              </Button>
            )}
          </Box>

          <Divider />

          <Box
            className="flex flex-col gap-2"
            sx={{ height: 250, overflowY: "auto" }}
          >
            <StatusChangeTimeLine
              isForReport
              eventModel={{
                CreationDate: requestModel?.CreationDate,
              }}
              timelineData={getStatusChangeData()}
            />
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ width: "100%" }}>
        {showAddModal && showAddStatusChangeModal()}

        <NoDataPage
          pageTitle={localize.no_status_changes}
          noHeight
          showAddButton
          onAddButtonClick={() => {
            addStatusChange();
          }}
        />
      </Box>
    );
  };

  useEffect(() => {
    getStatusChanges();
  }, []);

  return isLoading ? (
    <Box sx={{ width: "100%" }}>
      <LoadingPanel donotUseHscreen />
    </Box>
  ) : (
    getUI()
  );
}
